import { useContext, useEffect, useState } from "react";
import { BreadTransaction, P, Predicate } from "breadcommon";
import { TransactionColumn } from "./TransactionColumn";
import { TransactionsNavContext } from "./TransactionsScreen";
import { buildClasses } from "../utils/buildClasses";
import { RyeMonthPicker } from "../rye/RyeMonthPicker";
import { Dayjs } from "dayjs";
import { TransactionColumnFilterProps } from "./DefaultTransactionColumnHeader";

export function TransactionColumnFilterDate({
  isOpen,
  onClose,
  hasActiveFilter,
}: TransactionColumnFilterProps): JSX.Element | null {
  const [transactionsNav, setTransactionsNav] = useContext(
    TransactionsNavContext,
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (!hasActiveFilter) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [hasActiveFilter]);

  function createDateRangePredicate(
    start: Dayjs | null,
    end: Dayjs | null,
  ): Predicate | null {
    const dateField = P.Field<BreadTransaction, "date">("date");
    const dateFieldAsUnixTimestamp = P.ToUnixTimestamp(dateField);
    const predicates: Predicate[] = [];

    if (start) {
      // Start of the selected month
      const startOfMonth = start.startOf("month");
      predicates.push(
        P.GreaterThanEqualTo(
          dateFieldAsUnixTimestamp,
          P.Literal(startOfMonth.unix()),
        ),
      );
    }

    if (end) {
      // End of the selected month
      const endOfMonth = end.endOf("month");
      predicates.push(
        P.LessThanEqualTo(
          dateFieldAsUnixTimestamp,
          P.Literal(endOfMonth.unix()),
        ),
      );
    }

    if (predicates.length === 0) {
      return null;
    }

    return P.All(...predicates);
  }

  function applyFilter(start: Dayjs | null, end: Dayjs | null) {
    const currentSettings =
      transactionsNav.columnSettings[TransactionColumn.DATE];
    setTransactionsNav((prevState) => ({
      ...prevState,
      columnSettings: {
        ...transactionsNav.columnSettings,
        [TransactionColumn.DATE]: {
          ...currentSettings,
          predicate: createDateRangePredicate(start, end),
        },
      },
    }));
  }

  function handleStartDateChange(newStartDate: Dayjs | null) {
    setStartDate(newStartDate);
    applyFilter(newStartDate, endDate);
  }

  function handleEndDateChange(newEndDate: Dayjs | null) {
    setEndDate(newEndDate);
    applyFilter(startDate, newEndDate);
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className={buildClasses("flex", "items-center", "gap-2")}>
      <div className={buildClasses("shadow-md", "rounded-md", "bg-surface")}>
        <RyeMonthPicker
          selectedDate={startDate}
          onDateChange={handleStartDateChange}
          size="md"
          width="sm"
          placeholder="From"
          showClearButton
        />
      </div>
      <div className={buildClasses("shadow-md", "rounded-md", "bg-surface")}>
        <RyeMonthPicker
          selectedDate={endDate}
          onDateChange={handleEndDateChange}
          size="md"
          width="sm"
          placeholder="Through"
          showClearButton
        />
      </div>
    </div>
  );
}
